/**
 * RosterCards — a project's employees as a card grid (demo /teams member cards, NextAdmin card widget).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Card, Chip } from '../../../components/ui';
import { Avatar, StatusBadge, type EmployeeView } from '../shared';

interface RosterCardsProps {
  employees: EmployeeView[];
  hrefFor: (id: string) => string;
  projectName: string;
  linkedEmployeeIds: Map<string, unknown>;
}

export const RosterCards: React.FC<RosterCardsProps> = ({ employees, hrefFor, projectName, linkedEmployeeIds }) => (
  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
    {employees.map((emp) => {
      const linked = linkedEmployeeIds.has(emp.id);
      return (
        <Card key={emp.id} className={emp.attn ? 'border-danger' : undefined}>
          <div className="flex items-start gap-3">
            <Avatar label={emp.name} kind={emp.kind} initials={emp.initials} size={42} showKind />
            <div className="min-w-0 flex-1">
              <Link to={hrefFor(emp.id)} className="block truncate text-sm font-semibold text-heading hover:underline">
                {emp.name}
              </Link>
              <div className="truncate text-xs text-muted">
                {emp.role}
                {emp.tier ? ` · ${emp.tier}` : null}
              </div>
            </div>
            <StatusBadge status={emp.status} label={emp.statusLabel} compact />
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <Chip>{linked ? projectName : 'Reusable'}</Chip>
            {linked && emp.team && <Chip>{emp.team}</Chip>}
            {emp.workstream && <Chip>{emp.workstream}</Chip>}
          </div>

          <div className="mt-3 text-xs">
            {emp.blockerShort ? (
              <span className="inline-flex items-center gap-1 text-danger">
                <AlertTriangle size={12} />
                {emp.blockerShort}
              </span>
            ) : (
              <span className="text-body">{emp.currentChange ?? 'Idle'}</span>
            )}
            <div className="mt-1 text-muted">{emp.currentStep}</div>
          </div>
        </Card>
      );
    })}
  </div>
);
